import { useEffect, useMemo, useState, type ChangeEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { CATEGORIES } from '@air/shared';
import type { Category } from '@air/shared';
import { api, ApiError, type LookupHit, type NewRequestInput } from '../lib/api';
import { useToasts } from '../lib/useToasts';
import { useAuth } from '../auth/AuthContext';
import { Icon, StatusBadge, ToolLogo } from '../components/atoms';
import { STATUS_LABELS } from '@air/shared';

interface WizardState {
  tool: string;
  vendor: string;
  url: string;
  category: Category | '';
  logoUrl: string;
  description: string;
  inputs: string;
  outputs: string;
  outputUse: string;
  confidential: boolean | null;
  privileged: boolean | null;
  customer: boolean | null;
  notes: string;
}

const EMPTY: WizardState = {
  tool: '',
  vendor: '',
  url: '',
  category: '',
  logoUrl: '',
  description: '',
  inputs: '',
  outputs: '',
  outputUse: '',
  confidential: null,
  privileged: null,
  customer: null,
  notes: '',
};

const STEPS = [
  { id: 1, label: 'Software', overline: 'Step 1 of 4', title: 'What tool are you requesting?', desc: "The basics — tool name, vendor, and where to find it. We'll check the registry for duplicates as you type." },
  { id: 2, label: 'Purpose', overline: 'Step 2 of 4', title: 'How will you use it?', desc: 'Help reviewers understand the use case. Plain language is best — a few sentences per field is plenty.' },
  { id: 3, label: 'Data', overline: 'Step 3 of 4', title: 'What data will you put into it?', desc: 'These three questions decide who needs to review and which controls apply. When in doubt, say yes.' },
  { id: 4, label: 'Review', overline: 'Step 4 of 4', title: 'Review and submit', desc: "Take one last look. After submit, IT receives an email and you'll see a live timeline." },
] as const;

const STEP_FIELDS: Record<1 | 2 | 3 | 4, Array<keyof WizardState>> = {
  1: ['tool', 'url', 'category'],
  2: ['description', 'inputs', 'outputs'],
  3: ['confidential', 'privileged', 'customer', 'outputUse'],
  4: [],
};

const STEP_FOOTER: Record<1 | 2 | 3 | 4, string> = {
  1: 'After submit, an email goes to the IT Review queue.',
  2: 'Reviewers use these answers to assess risk.',
  3: 'If any answer is yes, Legal review is required after IT.',
  4: 'Draft is auto-saved. You can cancel anytime.',
};

export function SubmitWizardPage() {
  const navigate = useNavigate();
  const toasts = useToasts();
  const { user } = useAuth();
  const publicConfig = useQuery({
    queryKey: ['public-config'],
    queryFn: api.publicConfig,
    staleTime: 5 * 60_000,
  });
  const itEmail = publicConfig.data?.itContactEmail ?? '';
  const [step, setStep] = useState<1 | 2 | 3 | 4>(1);
  const [touched, setTouched] = useState<Partial<Record<keyof WizardState, boolean>>>({});
  const [form, setForm] = useState<WizardState>(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [dupes, setDupes] = useState<LookupHit[]>([]);
  const [serverError, setServerError] = useState<string | null>(null);

  // Debounced duplicate-detection on step 1
  useEffect(() => {
    const term = form.tool.trim();
    if (step !== 1 || term.length < 3) {
      setDupes([]);
      return;
    }
    const h = window.setTimeout(() => {
      api.lookup(term).then(setDupes).catch(() => setDupes([]));
    }, 300);
    return () => window.clearTimeout(h);
  }, [form.tool, step]);

  const errors = useMemo(() => validateAll(form, step), [form, step]);

  function setVal<K extends keyof WizardState>(key: K, value: WizardState[K]): void {
    setForm((f) => ({ ...f, [key]: value }));
  }
  function blur(key: keyof WizardState) {
    return () => setTouched((t) => ({ ...t, [key]: true }));
  }

  function canAdvance(): boolean {
    return STEP_FIELDS[step].every((f) => !errors[f]);
  }

  function next(): void {
    setTouched((t) => ({
      ...t,
      ...Object.fromEntries(STEP_FIELDS[step].map((f) => [f, true])),
    }));
    if (canAdvance() && step < 4) setStep((s) => (s + 1) as 1 | 2 | 3 | 4);
  }
  function prev(): void {
    if (step > 1) setStep((s) => (s - 1) as 1 | 2 | 3 | 4);
  }

  async function submit(): Promise<void> {
    if (
      form.confidential === null ||
      form.privileged === null ||
      form.customer === null ||
      !form.category
    ) {
      return;
    }
    setSubmitting(true);
    setServerError(null);
    const payload: NewRequestInput = {
      softwareName: form.tool.trim(),
      vendor: form.vendor.trim() || null,
      category: form.category,
      logoUrl: form.logoUrl.trim() || null,
      websiteUrl: form.url.trim(),
      intendedUse: form.description.trim(),
      dataInputs: form.inputs.trim(),
      expectedOutputs: form.outputs.trim(),
      usesConfidentialData: form.confidential,
      usesPrivilegedData: form.privileged,
      usesCustomerData: form.customer,
      outputUsage: form.outputUse.trim(),
      additionalNotes: form.notes.trim() || null,
    };
    try {
      const result = await api.createRequest(payload);
      toasts.push(`Request submitted — ticket ${result.request.ticketNumber}`);
      navigate(`/requests/${result.request.id}`, { replace: true });
    } catch (err) {
      setServerError(err instanceof ApiError ? err.message : 'Submission failed.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="reveal">
      <div className="page-headbar">
        <div>
          <div className="page-overline">Submit a Request</div>
          <h1 className="page-title">Submit an AI or software tool for approval</h1>
          <p className="page-desc">
            Takes about three minutes. Your request flows Employee → IT Review → Legal Review.
            You'll always see exactly where it stands.
          </p>
        </div>
        <Link className="btn btn--ghost" to="/dashboard">Cancel</Link>
      </div>

      <div className="wizard">
        <div className="wizard__stepper">
          {STEPS.map((s, i) => {
            const cls =
              s.id < step ? 'wizard__step--done' :
              s.id === step ? 'wizard__step--current' : 'wizard__step--pending';
            return (
              <div
                key={s.id}
                className={`wizard__step ${cls}`}
                onClick={() => s.id < step && setStep(s.id as 1 | 2 | 3 | 4)}
                style={{ cursor: s.id < step ? 'pointer' : 'default' }}
              >
                {i > 0 && (
                  <div
                    className={`wizard__connector ${s.id <= step ? 'wizard__connector--done' : ''}`}
                    style={{ left: 'calc(-50% + 15px)', right: 'calc(50% + 15px)' }}
                  />
                )}
                <div className="wizard__node">{s.id < step ? '✓' : s.id}</div>
                <div className="wizard__step-label">{s.label}</div>
              </div>
            );
          })}
        </div>

        <div className="wizard__body">
          {step === 1 && (
            <Step1
              form={form}
              setVal={setVal}
              blur={blur}
              touched={touched}
              errors={errors}
              dupes={dupes}
            />
          )}
          {step === 2 && (
            <Step2 form={form} setVal={setVal} blur={blur} touched={touched} errors={errors} />
          )}
          {step === 3 && (
            <Step3 form={form} setVal={setVal} touched={touched} errors={errors} />
          )}
          {step === 4 && (
            <Step4 form={form} setVal={setVal} onEdit={(s) => setStep(s)} itEmail={itEmail} />
          )}
          {serverError && (
            <div className="alert alert--danger" style={{ marginTop: 18 }}>
              <strong>Submission failed.</strong>&nbsp;{serverError}
            </div>
          )}
        </div>

        <div className="wizard__footer">
          <div className="wizard__footer-info">{STEP_FOOTER[step]}</div>
          <div style={{ display: 'flex', gap: 10 }}>
            {step > 1 && (
              <button className="btn btn--secondary" onClick={prev}>← Back</button>
            )}
            {step < 4 && (
              <button className="btn btn--primary" onClick={next}>
                Continue →
              </button>
            )}
            {step === 4 && (
              <button
                className="btn btn--primary btn--lg"
                onClick={() => void submit()}
                disabled={submitting}
              >
                {submitting ? 'Submitting…' : 'Submit a Request'}
              </button>
            )}
          </div>
        </div>
      </div>
      {/* Suppress unused-warning until we surface user info inline */}
      <span hidden>{user?.email}</span>
    </div>
  );
}

// ── Validation ──
function validateAll(f: WizardState, step: 1 | 2 | 3 | 4): Partial<Record<keyof WizardState, string>> {
  const e: Partial<Record<keyof WizardState, string>> = {};
  if (step >= 1) {
    if (!f.tool.trim()) e.tool = 'Tool name is required.';
    if (!f.url.trim()) e.url = 'Website URL is required.';
    else if (!/^https?:\/\/\S+/.test(f.url.trim())) e.url = 'URL should start with http:// or https://';
    if (!f.category) e.category = 'Pick the closest category.';
  }
  if (step >= 2) {
    if (f.description.trim().length < 20) e.description = 'Add at least a couple of sentences (20+ chars).';
    if (f.inputs.trim().length < 10) e.inputs = "Describe what data goes in.";
    if (f.outputs.trim().length < 10) e.outputs = 'Describe what the tool returns.';
  }
  if (step >= 3) {
    if (f.confidential === null) e.confidential = 'Please answer.';
    if (f.privileged === null) e.privileged = 'Please answer.';
    if (f.customer === null) e.customer = 'Please answer.';
    if (f.outputUse.trim().length < 10) e.outputUse = 'Describe how outputs will be used.';
  }
  return e;
}

// ── Step 1: Software ──
type SetVal = <K extends keyof WizardState>(k: K, v: WizardState[K]) => void;
type BlurFor = (k: keyof WizardState) => () => void;
type Touched = Partial<Record<keyof WizardState, boolean>>;
type Errors = Partial<Record<keyof WizardState, string>>;

function Step1({
  form,
  setVal,
  blur,
  touched,
  errors,
  dupes,
}: Readonly<{
  form: WizardState;
  setVal: SetVal;
  blur: BlurFor;
  touched: Touched;
  errors: Errors;
  dupes: LookupHit[];
}>) {
  const meta = STEPS[0];
  return (
    <div>
      <div className="wizard__step-overline">{meta.overline}</div>
      <h2 className="wizard__step-title">{meta.title}</h2>
      <p className="wizard__step-desc">{meta.desc}</p>

      {dupes.length > 0 && (
        <div className="dup-notice">
          <div className="dup-notice__icon">!</div>
          <div style={{ flex: 1 }}>
            <p className="dup-notice__title">Looks like this tool is already in the registry</p>
            <p className="dup-notice__body">
              <strong>{dupes[0]!.softwareName}</strong> — status:{' '}
              <StatusBadge status={dupes[0]!.status} /> ({STATUS_LABELS[dupes[0]!.status]}).
              You may not need to submit again.
            </p>
          </div>
          <Link to={`/requests/${dupes[0]!.id}`} className="btn btn--secondary btn--sm">
            View request
          </Link>
        </div>
      )}

      <div className="wizard__grid wizard__grid--2">
        <div className={`field ${touched.tool && errors.tool ? 'field--error' : ''}`}>
          <label className="field__label">
            Tool name <span className="field__req">*</span>
          </label>
          <p className="field__help">
            The product name as the vendor publishes it (e.g. "Talkatoo", "ChatGPT Enterprise").
          </p>
          <input
            type="text"
            placeholder="Talkatoo Veterinary Scribe"
            value={form.tool}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setVal('tool', e.target.value)}
            onBlur={blur('tool')}
          />
          {touched.tool && errors.tool && <div className="field__error">{errors.tool}</div>}
        </div>

        <div className="field">
          <label className="field__label">Vendor / company</label>
          <p className="field__help">Optional — who makes the tool. We can usually fill this in for you.</p>
          <input
            type="text"
            placeholder="Talkatoo Inc."
            value={form.vendor}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setVal('vendor', e.target.value)}
          />
        </div>

        <div
          className={`field ${touched.url && errors.url ? 'field--error' : ''}`}
          style={{ gridColumn: '1 / -1' }}
        >
          <label className="field__label">
            Website URL <span className="field__req">*</span>
          </label>
          <p className="field__help">Vendor homepage or product page. Reviewers use this to find docs and policies.</p>
          <input
            type="url"
            placeholder="https://talkatoo.com"
            value={form.url}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setVal('url', e.target.value)}
            onBlur={blur('url')}
          />
          {touched.url && errors.url && <div className="field__error">{errors.url}</div>}
        </div>

        <div
          className={`field ${touched.category && errors.category ? 'field--error' : ''}`}
          style={{ gridColumn: '1 / -1' }}
        >
          <label className="field__label">
            Category <span className="field__req">*</span>
          </label>
          <p className="field__help">
            Pick the closest match — this routes the request to the right specialists in IT and Legal.
          </p>
          <select
            value={form.category}
            onChange={(e: ChangeEvent<HTMLSelectElement>) => setVal('category', e.target.value as Category | '')}
            onBlur={blur('category')}
          >
            <option value="">Select a category…</option>
            {CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
          {touched.category && errors.category && <div className="field__error">{errors.category}</div>}
        </div>

        <div className="field" style={{ gridColumn: '1 / -1' }}>
          <label className="field__label">Logo (optional)</label>
          <p className="field__help">
            Paste a link to the vendor's logo or upload an image (PNG / SVG / JPG / WEBP / GIF, max
            250 KB). Leave blank and we'll generate a monogram from the tool name.
          </p>
          <LogoPicker
            value={form.logoUrl}
            toolName={form.tool}
            onChange={(v) => setVal('logoUrl', v)}
          />
        </div>
      </div>
    </div>
  );
}

// ── Step 2: Purpose ──
function Step2({
  form,
  setVal,
  blur,
  touched,
  errors,
}: Readonly<{ form: WizardState; setVal: SetVal; blur: BlurFor; touched: Touched; errors: Errors }>) {
  const meta = STEPS[1];
  return (
    <div>
      <div className="wizard__step-overline">{meta.overline}</div>
      <h2 className="wizard__step-title">{meta.title}</h2>
      <p className="wizard__step-desc">{meta.desc}</p>

      <div className="wizard__grid" style={{ gap: 22 }}>
        <div className={`field ${touched.description && errors.description ? 'field--error' : ''}`}>
          <label className="field__label">
            Description of intended use <span className="field__req">*</span>
          </label>
          <p className="field__help">What you'll use it for, at what frequency, and which team is involved.</p>
          <textarea
            placeholder="Voice-to-text scribe to draft SOAP notes during patient exams. Used by the GP team, ~20 visits per day."
            value={form.description}
            onChange={(e) => setVal('description', e.target.value)}
            onBlur={blur('description')}
          />
          {touched.description && errors.description && (
            <div className="field__error">{errors.description}</div>
          )}
        </div>

        <div className={`field ${touched.inputs && errors.inputs ? 'field--error' : ''}`}>
          <label className="field__label">
            Inputs / data you'll enter <span className="field__req">*</span>
          </label>
          <p className="field__help">Be specific: audio, text, images, patient data, internal docs, etc.</p>
          <textarea
            placeholder="Dictated clinical findings during exam, patient identifier, history. No client billing or contact info."
            value={form.inputs}
            onChange={(e) => setVal('inputs', e.target.value)}
            onBlur={blur('inputs')}
          />
          {touched.inputs && errors.inputs && <div className="field__error">{errors.inputs}</div>}
        </div>

        <div className={`field ${touched.outputs && errors.outputs ? 'field--error' : ''}`}>
          <label className="field__label">
            Expected outputs / results <span className="field__req">*</span>
          </label>
          <p className="field__help">What does the tool give back? Reports, suggestions, generated images, code?</p>
          <textarea
            placeholder="A structured SOAP note draft (subjective/objective/assessment/plan) ready for clinician edit."
            value={form.outputs}
            onChange={(e) => setVal('outputs', e.target.value)}
            onBlur={blur('outputs')}
          />
          {touched.outputs && errors.outputs && <div className="field__error">{errors.outputs}</div>}
        </div>
      </div>
    </div>
  );
}

// ── Step 3: Data Sensitivity ──
function Step3({
  form,
  setVal,
  touched,
  errors,
}: Readonly<{ form: WizardState; setVal: SetVal; touched: Touched; errors: Errors }>) {
  const meta = STEPS[2];
  const anyYes = form.confidential || form.privileged || form.customer;
  return (
    <div>
      <div className="wizard__step-overline">{meta.overline}</div>
      <h2 className="wizard__step-title">{meta.title}</h2>
      <p className="wizard__step-desc">{meta.desc}</p>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
        <YesNoRow
          label="Will confidential VIN data be used?"
          help="Internal docs, unpublished CE materials, financials, strategy. Excludes member or customer data."
          name="confidential"
          value={form.confidential}
          setVal={setVal}
          touched={touched}
          error={errors.confidential}
        />
        <YesNoRow
          label="Will privileged or legal data be used?"
          help="Attorney-client material, litigation records, NDA-protected content."
          name="privileged"
          value={form.privileged}
          setVal={setVal}
          touched={touched}
          error={errors.privileged}
        />
        <YesNoRow
          label="Will customer, member, or end-user data be used?"
          help="Member profile info, patient records, contact lists, anything that traces back to an identifiable person."
          name="customer"
          value={form.customer}
          setVal={setVal}
          touched={touched}
          error={errors.customer}
        />

        <div className={`field ${touched.outputUse && errors.outputUse ? 'field--error' : ''}`}>
          <label className="field__label">
            How will the outputs be used? <span className="field__req">*</span>
          </label>
          <p className="field__help">
            Internal-only? Shared with members? Acted on without review? Be honest — this helps the right controls get applied.
          </p>
          <textarea
            placeholder="Drafts are reviewed and edited by the attending DVM before being saved into the EMR. Never used unedited."
            value={form.outputUse}
            onChange={(e) => setVal('outputUse', e.target.value)}
          />
          {touched.outputUse && errors.outputUse && (
            <div className="field__error">{errors.outputUse}</div>
          )}
        </div>

        {anyYes && (
          <div className="alert alert--info">
            <Icon name="info-question" size={16} />
            <div>
              <strong>Heads up:</strong> because sensitive data is involved, Legal review will be
              required after IT approval. Median Legal review time is currently ~5 business days.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function YesNoRow({
  label,
  help,
  name,
  value,
  setVal,
  touched,
  error,
}: Readonly<{
  label: string;
  help: string;
  name: 'confidential' | 'privileged' | 'customer';
  value: boolean | null;
  setVal: SetVal;
  touched: Touched;
  error: string | undefined;
}>) {
  return (
    <div className={`field ${touched[name] && error ? 'field--error' : ''}`}>
      <label className="field__label">
        {label} <span className="field__req">*</span>
      </label>
      <p className="field__help">{help}</p>
      <div className="yesno">
        <label
          className={`yesno__opt ${value === true ? 'is-selected is-yes' : ''}`}
          onClick={() => setVal(name, true)}
        >
          <input type="radio" name={name} checked={value === true} onChange={() => setVal(name, true)} />
          <div>
            <p className="yesno__title">Yes</p>
            <p className="yesno__sub">Sensitive data may be entered.</p>
          </div>
        </label>
        <label
          className={`yesno__opt ${value === false ? 'is-selected' : ''}`}
          onClick={() => setVal(name, false)}
        >
          <input
            type="radio"
            name={name}
            checked={value === false}
            onChange={() => setVal(name, false)}
          />
          <div>
            <p className="yesno__title">No</p>
            <p className="yesno__sub">Only public or non-sensitive content.</p>
          </div>
        </label>
      </div>
      {touched[name] && error && <div className="field__error">{error}</div>}
    </div>
  );
}

// ── Step 4: Review ──
function Step4({
  form,
  setVal,
  onEdit,
  itEmail,
}: Readonly<{
  form: WizardState;
  setVal: SetVal;
  onEdit: (s: 1 | 2 | 3 | 4) => void;
  itEmail: string;
}>) {
  const meta = STEPS[3];
  return (
    <div>
      <div className="wizard__step-overline">{meta.overline}</div>
      <h2 className="wizard__step-title">{meta.title}</h2>
      <p className="wizard__step-desc">{meta.desc}</p>

      <ReviewBlock title="Software" step={1} onEdit={onEdit}>
        <ReviewRow label="Tool name" value={form.tool} />
        <ReviewRow label="Vendor" value={form.vendor || '—'} />
        <ReviewRow
          label="Website"
          value={
            <a href={form.url} target="_blank" rel="noreferrer">
              {form.url}
            </a>
          }
        />
        <ReviewRow label="Category" value={form.category || '—'} />
        <ReviewRow
          label="Logo"
          value={
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <ToolLogo name={form.tool || 'Tool'} logoUrl={form.logoUrl.trim() || undefined} size={32} />
              <span style={{ fontSize: 12, color: 'var(--color-steel)' }}>
                {form.logoUrl.trim() || 'Auto-generated monogram'}
              </span>
            </div>
          }
        />
      </ReviewBlock>

      <ReviewBlock title="Purpose" step={2} onEdit={onEdit}>
        <ReviewRow label="Intended use" value={form.description} />
        <ReviewRow label="Inputs / data in" value={form.inputs} />
        <ReviewRow label="Expected outputs" value={form.outputs} />
      </ReviewBlock>

      <ReviewBlock title="Data Sensitivity" step={3} onEdit={onEdit}>
        <ReviewRow label="Confidential data" value={yesno(form.confidential)} />
        <ReviewRow label="Privileged / legal" value={yesno(form.privileged)} />
        <ReviewRow label="Customer / member" value={yesno(form.customer)} />
        <ReviewRow label="Output usage" value={form.outputUse} />
      </ReviewBlock>

      <div className="field" style={{ marginTop: 18 }}>
        <label className="field__label">Additional notes (optional)</label>
        <p className="field__help">
          Anything else reviewers should know — BAA on file, pilot results, existing vendor relationship.
        </p>
        <textarea
          placeholder="Vendor offers a HIPAA-aligned BAA…"
          value={form.notes}
          onChange={(e) => setVal('notes', e.target.value)}
        />
      </div>

      <div className="alert alert--info" style={{ marginTop: 22 }}>
        <Icon name="info-question" size={16} />
        <div>
          <strong>What happens next:</strong> on submit, you'll get a ticket number, the request
          enters the IT review queue, and an email goes to{' '}
          <span className="kbd">{itEmail}</span>. You'll see every update on the request page.
        </div>
      </div>
    </div>
  );
}

function ReviewBlock({
  title,
  step,
  onEdit,
  children,
}: Readonly<{ title: string; step: 1 | 2 | 3 | 4; onEdit: (s: 1 | 2 | 3 | 4) => void; children: React.ReactNode }>) {
  return (
    <div className="review-section">
      <div className="review-section__head">
        <h3 className="review-section__title">{title}</h3>
        <button className="review-section__edit" onClick={() => onEdit(step)}>
          <Icon name="edit-penpaper" size={12} /> Edit
        </button>
      </div>
      {children}
    </div>
  );
}

function ReviewRow({ label, value }: Readonly<{ label: string; value: React.ReactNode }>) {
  return (
    <div className="review-section__row">
      <div className="review-section__label">{label}</div>
      <div className="review-section__value">{value || <span className="muted">—</span>}</div>
    </div>
  );
}

function yesno(v: boolean | null): string {
  if (v === null) return '—';
  return v ? 'Yes' : 'No';
}

const MAX_LOGO_BYTES = 250 * 1024;
const LOGO_MIME = /^image\/(png|jpe?g|gif|svg\+xml|webp)$/i;
const LOGO_EXT = /\.(png|jpe?g|gif|svg|webp)$/i;
const LOGO_EXT_TO_MIME: Record<string, string> = {
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  gif: 'image/gif',
  svg: 'image/svg+xml',
  webp: 'image/webp',
};

function LogoPicker({
  value,
  toolName,
  onChange,
}: Readonly<{ value: string; toolName: string; onChange: (v: string) => void }>) {
  const isData = value.startsWith('data:');
  const [mode, setMode] = useState<'url' | 'upload'>(isData ? 'upload' : 'url');
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [reading, setReading] = useState(false);
  const [uploadInfo, setUploadInfo] = useState<{ name: string; sizeKb: number } | null>(null);

  function onFile(file: File | null): void {
    setUploadError(null);
    if (!file) return;
    const extMatch = file.name.match(LOGO_EXT);
    const ext = extMatch?.[1]?.toLowerCase();
    const mimeOk = LOGO_MIME.test(file.type);
    const extOk = !!ext && ext in LOGO_EXT_TO_MIME;
    if (!mimeOk && !extOk) {
      setUploadError('Unsupported image type. Use PNG, JPG, GIF, SVG, or WEBP.');
      return;
    }
    if (file.size > MAX_LOGO_BYTES) {
      setUploadError(`Image is ${(file.size / 1024).toFixed(0)} KB — max is 250 KB.`);
      return;
    }
    setReading(true);
    const reader = new FileReader();
    reader.onload = () => {
      setReading(false);
      if (typeof reader.result !== 'string') {
        setUploadError('Could not read that file. Try another.');
        return;
      }
      let dataUrl = reader.result;
      // Rewrite the data URL prefix if the OS gave us an empty/non-image MIME but the
      // extension is one we accept (common for .svg on macOS Chrome).
      if (!mimeOk && extOk) {
        const targetMime = LOGO_EXT_TO_MIME[ext!]!;
        const commaIdx = dataUrl.indexOf(',');
        if (commaIdx > 0) dataUrl = `data:${targetMime};base64,${dataUrl.slice(commaIdx + 1)}`;
      }
      onChange(dataUrl);
      setUploadInfo({ name: file.name, sizeKb: Math.max(1, Math.round(file.size / 1024)) });
    };
    reader.onerror = () => {
      setReading(false);
      setUploadError('Could not read that file. Try another.');
    };
    reader.readAsDataURL(file);
  }

  return (
    <div>
      <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
        <button
          type="button"
          className={`chip ${mode === 'url' ? 'is-active' : ''}`}
          onClick={() => {
            setMode('url');
            setUploadError(null);
            if (isData) onChange('');
          }}
        >
          URL
        </button>
        <button
          type="button"
          className={`chip ${mode === 'upload' ? 'is-active' : ''}`}
          onClick={() => {
            setMode('upload');
            setUploadError(null);
            if (!isData && value) onChange('');
          }}
        >
          Upload
        </button>
      </div>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
        {mode === 'url' ? (
          <input
            type="url"
            placeholder="https://logo.clearbit.com/notion.so"
            value={isData ? '' : value}
            onChange={(e: ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
            style={{ flex: 1 }}
          />
        ) : (
          <div style={{ flex: 1, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <input
              type="file"
              accept=".png,.jpg,.jpeg,.gif,.svg,.webp,image/png,image/jpeg,image/gif,image/svg+xml,image/webp"
              onChange={(e: ChangeEvent<HTMLInputElement>) => onFile(e.target.files?.[0] ?? null)}
            />
            {reading && <span className="muted">Reading file…</span>}
            {!reading && isData && (
              <>
                <span style={{ color: 'var(--color-green, #006850)', fontWeight: 600 }}>
                  ✓ Logo ready{uploadInfo ? ` — ${uploadInfo.name} (${uploadInfo.sizeKb} KB)` : ''}
                </span>
                <button
                  type="button"
                  className="btn btn--ghost btn--sm"
                  onClick={() => {
                    onChange('');
                    setUploadInfo(null);
                  }}
                >
                  Remove
                </button>
              </>
            )}
          </div>
        )}
        <ToolLogo name={toolName || 'Tool'} logoUrl={value.trim() || undefined} size={48} />
      </div>
      {uploadError && (
        <div className="field__error" style={{ marginTop: 6 }}>
          {uploadError}
        </div>
      )}
    </div>
  );
}
